import type { JSX, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { AuthContext, type AuthContextValue } from '../context/AuthContext';
import { getSupabaseClient } from '../lib/supabase';
import { Logger } from '../utils/logger';

const logger = new Logger('AuthProvider');

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const supabase = getSupabaseClient();

    // Get the initial session from cookies
    const initializeAuth = async () => {
      try {
        const {
          data: { session: initialSession },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          logger.error('Error getting session', sessionError);
          setError(sessionError);
        } else {
          setSession(initialSession);
          setUser(initialSession?.user ?? null);
        }
      } catch (err) {
        logger.error('Error initializing auth', err);
        setError(err instanceof Error ? err : new Error('Failed to initialize auth'));
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setError(null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signInAnonymously = useCallback(async (captchaToken?: string) => {
    const supabase = getSupabaseClient();
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: signInError } = await supabase.auth.signInAnonymously({
        options: captchaToken ? { captchaToken } : undefined,
      });

      if (signInError) {
        throw signInError;
      }

      setSession(data.session);
      setUser(data.user);
    } catch (err) {
      const authError = err instanceof Error ? err : new Error('Failed to sign in anonymously');
      setError(authError);
      throw authError;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    const supabase = getSupabaseClient();
    setIsLoading(true);
    setError(null);

    try {
      const { error: signOutError } = await supabase.auth.signOut();

      if (signOutError) {
        throw signOutError;
      }

      setSession(null);
      setUser(null);
    } catch (err) {
      const authError = err instanceof Error ? err : new Error('Failed to sign out');
      setError(authError);
      throw authError;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const linkEmail = useCallback(async (email: string, metadata?: Record<string, string>) => {
    const supabase = getSupabaseClient();
    setError(null);

    const { data, error: updateError } = await supabase.auth.updateUser({
      email,
      ...(metadata && { data: metadata }),
    });

    if (updateError) throw updateError;
    if (data.user) setUser(data.user);
  }, []);

  const verifyEmailOtp = useCallback(async (email: string, token: string) => {
    const supabase = getSupabaseClient();
    setError(null);

    const { data, error: verifyError } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email_change',
    });

    if (verifyError) throw verifyError;
    if (data.session) setSession(data.session);
    if (data.user) setUser(data.user);
  }, []);

  const resendEmailOtp = useCallback(async (email: string) => {
    const supabase = getSupabaseClient();
    setError(null);

    // supabase.auth.resend({ type: 'email_change' }) silently returns {}
    // without actually sending. Re-issuing updateUser triggers a new OTP.
    const { error: resendError } = await supabase.auth.updateUser({ email });

    if (resendError) throw resendError;
  }, []);

  const updateProfile = useCallback(async (data: Record<string, string>) => {
    const supabase = getSupabaseClient();
    setError(null);

    const { data: result, error: updateError } = await supabase.auth.updateUser({ data });

    if (updateError) throw updateError;
    if (result.user) setUser(result.user);
  }, []);

  const sendLoginOtp = useCallback(
    async (email: string, options?: { metadata?: Record<string, string>; shouldCreateUser?: boolean }) => {
      const supabase = getSupabaseClient();
      setError(null);

      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          ...(options?.metadata && { data: options.metadata }),
          shouldCreateUser: options?.shouldCreateUser ?? false,
        },
      });

      if (otpError) throw otpError;
    },
    []
  );

  const verifyLoginOtp = useCallback(
    async (email: string, token: string, metadata?: { fullName?: string; phone?: string }) => {
      const supabase = getSupabaseClient();
      setError(null);

      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });

      if (verifyError) throw verifyError;

      if (metadata?.fullName || metadata?.phone) {
        const updatePayload: { phone?: string; data?: Record<string, string> } = {};
        if (metadata.phone) updatePayload.phone = metadata.phone;
        if (metadata.fullName) updatePayload.data = { full_name: metadata.fullName };
        const { data: updated, error: updateError } = await supabase.auth.updateUser(updatePayload);
        if (!updateError && updated.user) {
          setUser(updated.user);
          if (data.session) setSession(data.session);
          return;
        }
      }

      if (data.user) setUser(data.user);
      if (data.session) setSession(data.session);
    },
    []
  );

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      session,
      isLoading,
      error,
      signInAnonymously,
      signOut,
      linkEmail,
      verifyEmailOtp,
      resendEmailOtp,
      updateProfile,
      sendLoginOtp,
      verifyLoginOtp,
    }),
    [
      user,
      session,
      isLoading,
      error,
      signInAnonymously,
      signOut,
      linkEmail,
      verifyEmailOtp,
      resendEmailOtp,
      updateProfile,
      sendLoginOtp,
      verifyLoginOtp,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
